<template>
  <div class="form-section">
    <Subtitle>Open Case</Subtitle>
    <div>
      <EntitySearchWithActions
        v-if="showClientSelector"
        label="Client (optional)"
        hint="Leave this blank to create a general case not tied to a specific client."
        :current="selectedEntityOption"
        :disabled="isSubmittingCase"
        secondary-action-text="Clear"
        @select="selectSearchResult"
        @clear="clearSelectedEntity"
      />
      <Field v-else>
        <Label>Client</Label>
        <TextInput
          :value="lockedEntityName"
          type="text"
          disabled
        />
      </Field>

      <CaseRequestFields
        :fields="form.fields"
        :priority="form.priority"
        mode="edit"
        @update:field="updateCaseField"
        @update:priority="updatePriority"
      />
      <CaseCommentAttachmentComposer
        :new-comment="initialComment"
        :pending-attachment-files="pendingAttachmentFiles"
        :is-submitting-comment="isSubmittingCase"
        :is-submitting-attachment="isSubmittingCase"
        :allow-comment-submission="true"
        :allow-attachment-upload="true"
        :show-comment-submit-button="false"
        :show-attachment-submit-button="false"
        comment-placeholder="Add a comment to include with the case submission"
        pending-attachment-heading="Ready to upload after submit"
        @update:new-comment="updateInitialComment"
        @update:pending-attachment-files="updatePendingAttachmentFiles"
      />
    </div>

    <div class="form-actions">
      <Button
        type="primary"
        :disabled="isSubmittingCase"
        @click="submitCase"
      >
        <span
          v-if="isSubmittingCase"
          class="button-loading-content"
        >
          <i
            class="fa fa-spinner fa-spin"
            aria-hidden="true"
          />
          <span>Submitting Case...</span>
        </span>
        <span v-else>Submit Case</span>
      </Button>
      <Button
        type="secondary"
        @click="$emit('cancel')"
      >
        Cancel
      </Button>
    </div>
    <p
      v-if="formError"
      class="form-error"
    >
      {{ formError }}
    </p>
  </div>
</template>

<script>
import {
  Button,
  Field,
  Label,
  Subtitle,
  TextInput,
} from '@stakeui';
import CaseCommentAttachmentComposer from '@frontend/ui/features/example/components/CaseCommentAttachmentComposer.vue';
import CaseRequestFields from '@frontend/ui/features/example/components/CaseRequestFields.vue';
import EntitySearchWithActions from '@frontend/ui/features/example/components/EntitySearchWithActions.vue';
import {
  getJsonHeaders,
  uploadCaseAttachments,
} from '@frontend/ui/features/example/utils/caseActivityApi';
import * as Sentry from '@sentry/vue';

const emptyCaseFields = () => ({
  subject: '',
  request_category: '',
  service_case_type: '',
  trading_case_type: '',
  new_account_custodian: '',
  new_account_account_type: '',
  new_account_description: '',
  close_account_number: '',
  close_account_custodian: '',
  close_account_sec_reason: '',
  close_account_description: '',
  close_account_scope: '',
  orion_request_custodian: '',
  orion_request_account_identifier: '',
  orion_request_details: '',
  client_record_change_type: '',
  client_record_change_details: '',
  client_record_change_account_identifier: '',
  client_record_change_custodian: '',
  business_development_type: '',
  business_development_details: '',
  advisor_org_maintenance_type: '',
  advisor_org_maintenance_details: '',
  questions_case_type: '',
  fafn_question: '',
  compliance_case_type: '',
  compliance_details: '',
  compliance_account_number: '',
  compliance_custodian: '',
  distribution_amount: '',
  distribution_account_number: '',
  distribution_delivery_instructions: '',
  distribution_modify_recurring: '',
  distribution_custodian: '',
  cash_transfer_into_existing_account: '',
  cash_amount: '',
  cash_is_recurring: '',
  cash_account_number: '',
  cash_custodian: '',
  trade_request_type: '',
  trade_request_account_numbers: '',
  trade_request_instructions: '',
  trade_request_custodian: '',
  ips_request_type: '',
  ips_account_number: '',
  ips_custodian: '',
  ips_request_details: '',
  trade_review_type: '',
  trade_review_account_number: '',
  trade_review_custodian: '',
  trade_review_instructions: '',
  advocacy_account_number: '',
  advocacy_custodian: '',
  advocacy_instruction: '',
  vadis_question: '',
  reoptimization_objective: '',
  reoptimization_account_identifier: '',
  reoptimization_custodian: '',
  reoptimization_instruction: '',
});

export default {
  name: 'CaseSubmissionForm',
  components: {
    Button,
    CaseCommentAttachmentComposer,
    CaseRequestFields,
    EntitySearchWithActions,
    Field,
    Label,
    Subtitle,
    TextInput,
  },
  props: {
    submitUrl: {
      type: String,
      required: true,
    },
    showClientSelector: {
      type: Boolean,
      default: false,
    },
    lockedEntityName: {
      type: String,
      default: '',
    },
    lockedEntityExternalId: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      selectedEntityExternalId: '',
      selectedEntityOption: null,
      initialComment: '',
      pendingAttachmentFiles: [],
      isSubmittingCase: false,
      formError: '',
      form: {
        priority: '',
        type: 'General Support',
        fields: emptyCaseFields(),
      },
    };
  },
  computed: {
    effectiveEntityExternalId() {
      if (this.showClientSelector) {
        return this.selectedEntityExternalId;
      }
      return this.lockedEntityExternalId;
    },
    effectiveEntityName() {
      if (this.showClientSelector) {
        return (this.selectedEntityOption?.name || '').trim();
      }
      return (this.lockedEntityName || '').trim();
    },
    requiresEntitySelection() {
      return this.isNewAccountCase ||
        this.isCloseAccountCase ||
        this.isOrionRequestCase ||
        this.isClientRecordChangeCase ||
        this.isRaiseCashDistributeCase ||
        this.isCashAmountCase ||
        this.isTradeRequestCase ||
        this.isIpsRequestCase ||
        this.isTradeReviewCase ||
        this.isAdvocacyCase ||
        this.isReoptimizationCase;
    },
    hasRequestCategory() {
      return String(this.form.fields.request_category || '').trim() !== '';
    },
    showStep2() {
      return this.hasRequestCategory && (this.isServiceOther || this.isTradingRelated);
    },
    showStep3() {
      if (!this.hasRequestCategory) {
        return false;
      }
      if (this.isNewAccountCase || this.isCloseAccountCase) {
        return true;
      }
      if (this.isServiceOther) {
        return String(this.form.fields.service_case_type || '').trim() !== '';
      }
      if (this.isTradingRelated) {
        return String(this.form.fields.trading_case_type || '').trim() !== '';
      }
      return false;
    },
    submissionRequestCategory() {
      if (!this.hasRequestCategory) {
        return '';
      }
      return this.form.fields.request_category;
    },
    isServiceOther() {
      return this.form.fields.request_category === 'service_other';
    },
    isTradingRelated() {
      return this.form.fields.request_category === 'trading_related';
    },
    isNewAccountCase() {
      return this.form.fields.request_category === 'new_account' ||
        this.form.fields.service_case_type === 'new_account';
    },
    isCloseAccountCase() {
      return this.form.fields.request_category === 'close_account' ||
        this.form.fields.service_case_type === 'close_account';
    },
    isOrionRequestCase() {
      return this.form.fields.service_case_type === 'orion_request';
    },
    isClientRecordChangeCase() {
      return this.form.fields.service_case_type === 'client_record_changes';
    },
    isBusinessDevelopmentCase() {
      return this.form.fields.service_case_type === 'business_development';
    },
    isAdvisorOrgMaintenanceCase() {
      return this.form.fields.service_case_type === 'advisor_org_maintenance';
    },
    isQuestionsCase() {
      return this.form.fields.service_case_type === 'questions';
    },
    isComplianceCase() {
      return this.form.fields.service_case_type === 'compliance';
    },
    isComplianceFraudAlertCase() {
      return this.form.fields.compliance_case_type === 'fraud_alert';
    },
    isRaiseCashDistributeCase() {
      return ['raise_cash_distribute', 'get_cash'].includes(this.form.fields.trading_case_type);
    },
    isCashAmountCase() {
      return this.form.fields.trading_case_type === 'add_cash';
    },
    isTradeRequestCase() {
      return this.form.fields.trading_case_type === 'trade_request_instructions';
    },
    isIpsRequestCase() {
      return this.form.fields.trading_case_type === 'ips_request';
    },
    isTradeReviewCase() {
      return this.form.fields.trading_case_type === 'trade_review';
    },
    isAdvocacyCase() {
      return this.form.fields.trading_case_type === 'advocacy';
    },
    isReoptimizationCase() {
      return this.form.fields.trading_case_type === 'reoptimization';
    },
    isVadisQuestionCase() {
      return this.form.fields.trading_case_type === 'vadis_question';
    },
    showDetailFields() {
      return [
        this.isNewAccountCase,
        this.isCloseAccountCase,
        this.isOrionRequestCase,
        this.isClientRecordChangeCase,
        this.isBusinessDevelopmentCase,
        this.isAdvisorOrgMaintenanceCase,
        this.isQuestionsCase,
        this.isComplianceCase,
        this.isRaiseCashDistributeCase,
        this.isCashAmountCase,
        this.isTradeRequestCase,
        this.isIpsRequestCase,
        this.isTradeReviewCase,
        this.isAdvocacyCase,
        this.isReoptimizationCase,
        this.isVadisQuestionCase,
      ].some(Boolean);
    },
    hasEnteredInformation() {
      const hasFieldValue = Object.values(this.form.fields).some(value => !this.isBlank(value));
      const hasPriority = !this.isBlank(this.form.priority);
      const hasComment = !this.isBlank(this.initialComment);
      const hasAttachments = this.pendingAttachmentFiles.length > 0;
      const hasSelectedEntity = this.showClientSelector && !this.isBlank(this.selectedEntityExternalId);

      return hasFieldValue ||
        hasPriority ||
        hasComment ||
        hasAttachments ||
        hasSelectedEntity;
    },
  },
  watch: {
    'form.fields.request_category'(newValue, oldValue) {
      if (newValue === oldValue) {
        return;
      }
      this.form.fields.service_case_type = '';
      this.form.fields.trading_case_type = '';
      this.clearServiceFields();
      this.clearTradingFields();
      if (newValue === 'new_account') {
        this.form.fields.service_case_type = 'new_account';
      } else if (newValue === 'close_account') {
        this.form.fields.service_case_type = 'close_account';
      }
    },
    'form.fields.service_case_type'(newValue, oldValue) {
      if (!oldValue || newValue === oldValue) {
        return;
      }
      this.clearServiceFields();
    },
    'form.fields.trading_case_type'(newValue, oldValue) {
      if (!oldValue || newValue === oldValue) {
        return;
      }
      this.clearTradingFields();
    },
    hasEnteredInformation: {
      immediate: true,
      handler(nextValue) {
        this.$emit('dirty-state-changed', nextValue);
      },
    },
  },
  methods: {
    clearSelectedEntity() {
      this.selectedEntityOption = null;
      this.selectedEntityExternalId = '';
    },
    selectSearchResult(option) {
      this.selectedEntityOption = option || null;
      this.selectedEntityExternalId = option?.external_id || '';
    },
    updateCaseField({ fieldName, value }) {
      this.form.fields[fieldName] = value;
    },
    updatePriority(value) {
      this.form.priority = value;
    },
    updateInitialComment(value) {
      this.initialComment = value;
    },
    updatePendingAttachmentFiles(nextFiles) {
      this.pendingAttachmentFiles = [...(nextFiles || [])];
    },
    async uploadAttachments(caseId) {
      await uploadCaseAttachments({
        url: `/api/v1/cases/${caseId}/attachments/`,
        files: this.pendingAttachmentFiles,
        csrfToken: this.$root.$data.csrfToken,
      });
    },
    clearServiceFields() {
      this.form.fields.new_account_custodian = '';
      this.form.fields.new_account_account_type = '';
      this.form.fields.close_account_number = '';
      this.form.fields.close_account_custodian = '';
      this.form.fields.close_account_sec_reason = '';
      this.form.fields.close_account_description = '';
      this.form.fields.close_account_scope = '';
      this.form.fields.orion_request_custodian = '';
      this.form.fields.orion_request_account_identifier = '';
      this.form.fields.orion_request_details = '';
      this.form.fields.client_record_change_type = '';
      this.form.fields.client_record_change_details = '';
      this.form.fields.client_record_change_account_identifier = '';
      this.form.fields.client_record_change_custodian = '';
      this.form.fields.business_development_type = '';
      this.form.fields.business_development_details = '';
      this.form.fields.advisor_org_maintenance_type = '';
      this.form.fields.advisor_org_maintenance_details = '';
      this.form.fields.questions_case_type = '';
      this.form.fields.fafn_question = '';
      this.form.fields.compliance_case_type = '';
      this.form.fields.compliance_details = '';
      this.form.fields.compliance_account_number = '';
      this.form.fields.compliance_custodian = '';
    },
    clearTradingFields() {
      this.form.fields.distribution_amount = '';
      this.form.fields.distribution_account_number = '';
      this.form.fields.distribution_delivery_instructions = '';
      this.form.fields.distribution_modify_recurring = '';
      this.form.fields.distribution_custodian = '';
      this.form.fields.cash_transfer_into_existing_account = '';
      this.form.fields.cash_amount = '';
      this.form.fields.cash_is_recurring = '';
      this.form.fields.cash_account_number = '';
      this.form.fields.cash_custodian = '';
      this.form.fields.trade_request_type = '';
      this.form.fields.trade_request_account_numbers = '';
      this.form.fields.trade_request_instructions = '';
      this.form.fields.trade_request_custodian = '';
      this.form.fields.ips_request_type = '';
      this.form.fields.ips_account_number = '';
      this.form.fields.ips_custodian = '';
      this.form.fields.ips_request_details = '';
      this.form.fields.trade_review_type = '';
      this.form.fields.trade_review_account_number = '';
      this.form.fields.trade_review_custodian = '';
      this.form.fields.trade_review_instructions = '';
      this.form.fields.advocacy_account_number = '';
      this.form.fields.advocacy_custodian = '';
      this.form.fields.advocacy_instruction = '';
      this.form.fields.vadis_question = '';
      this.form.fields.reoptimization_objective = '';
      this.form.fields.reoptimization_account_identifier = '';
      this.form.fields.reoptimization_custodian = '';
      this.form.fields.reoptimization_instruction = '';
    },
    isBlank(value) {
      return value === null || value === undefined || String(value).trim() === '';
    },
    validateStep(stepNumber) {
      if (stepNumber === 1) {
        if (this.isBlank(this.form.fields.request_category)) {
          return 'Please select a case submission type.';
        }
      } else if (stepNumber === 2) {
        if (this.isServiceOther && this.isBlank(this.form.fields.service_case_type)) {
          return 'Please select a service / other request type.';
        }
        if (this.isTradingRelated && this.isBlank(this.form.fields.trading_case_type)) {
          return 'Please select a trading request type.';
        }
      } else if (stepNumber === 3) {
        if (this.isNewAccountCase) {
          if (this.isBlank(this.form.fields.new_account_custodian)) {
            return 'Please select at least one custodian for the new account request.';
          }
          if (this.isBlank(this.form.fields.new_account_account_type)) {
            return 'Please select at least one account type for the new account request.';
          }
        } else if (this.isCloseAccountCase) {
          if (this.isBlank(this.form.fields.close_account_number)) {
            return 'Please enter an account number for the close account request.';
          }
          if (this.isBlank(this.form.fields.close_account_custodian)) {
            return 'Please select at least one custodian for the close account request.';
          }
          if (this.isBlank(this.form.fields.close_account_sec_reason)) {
            return 'Please provide the SEC reason for the close account request.';
          }
        } else if (this.isOrionRequestCase) {
          if (this.isBlank(this.form.fields.orion_request_custodian)) {
            return 'Please select at least one custodian for the Orion request.';
          }
          if (this.isBlank(this.form.fields.orion_request_account_identifier)) {
            return 'Please enter an account number or account name for the Orion request.';
          }
          if (this.isBlank(this.form.fields.orion_request_details)) {
            return 'Please enter the Orion question details.';
          }
        } else if (this.isClientRecordChangeCase) {
          if (this.isBlank(this.form.fields.client_record_change_type)) {
            return 'Please select a client record change type.';
          }
          if (this.isBlank(this.form.fields.client_record_change_account_identifier)) {
            return 'Please enter an account number or account name for the client record change.';
          }
          if (this.isBlank(this.form.fields.client_record_change_custodian)) {
            return 'Please select at least one custodian for the client record change.';
          }
          if (this.isBlank(this.form.fields.client_record_change_details)) {
            return 'Please provide details for the client record change.';
          }
        } else if (this.isBusinessDevelopmentCase) {
          if (this.isBlank(this.form.fields.business_development_type)) {
            return 'Please select a business development request type.';
          }
          if (this.isBlank(this.form.fields.business_development_details)) {
            return 'Please enter business development details.';
          }
        } else if (this.isAdvisorOrgMaintenanceCase) {
          if (this.isBlank(this.form.fields.advisor_org_maintenance_type)) {
            return 'Please select an advisor org maintenance request type.';
          }
          if (this.isBlank(this.form.fields.advisor_org_maintenance_details)) {
            return 'Please enter advisor org maintenance details.';
          }
        } else if (this.isQuestionsCase) {
          if (this.isBlank(this.form.fields.questions_case_type)) {
            return 'Please select a questions request type.';
          }
          if (this.isBlank(this.form.fields.fafn_question)) {
            return 'Please enter the question details.';
          }
        } else if (this.isComplianceCase) {
          if (this.isBlank(this.form.fields.compliance_case_type)) {
            return 'Please select a compliance request type.';
          }
          if (this.isComplianceFraudAlertCase) {
            if (this.isBlank(this.form.fields.compliance_account_number)) {
              return 'Please enter an account number for the fraud alert request.';
            }
            if (this.isBlank(this.form.fields.compliance_custodian)) {
              return 'Please select at least one custodian for the fraud alert request.';
            }
          } else if (this.isBlank(this.form.fields.compliance_details)) {
            return 'Please enter compliance review details.';
          }
        } else if (this.isRaiseCashDistributeCase) {
          if (this.isBlank(this.form.fields.distribution_amount)) {
            return 'Please enter the distribution amount.';
          }
          if (this.isBlank(this.form.fields.distribution_account_number)) {
            return 'Please enter the destination account number.';
          }
          if (this.isBlank(this.form.fields.distribution_delivery_instructions)) {
            return 'Please provide delivery instructions.';
          }
          if (this.isBlank(this.form.fields.distribution_custodian)) {
            return 'Please select at least one custodian for the distribution.';
          }
        } else if (this.isCashAmountCase) {
          if (this.isBlank(this.form.fields.cash_transfer_into_existing_account)) {
            return 'Please specify whether this is a transfer into an existing account.';
          }
          if (this.isBlank(this.form.fields.cash_amount)) {
            return 'Please enter the cash amount.';
          }
          if (this.isBlank(this.form.fields.cash_account_number)) {
            return 'Please enter an account number for the cash request.';
          }
          if (this.isBlank(this.form.fields.cash_custodian)) {
            return 'Please select at least one custodian for the cash request.';
          }
          if (this.isBlank(this.form.fields.cash_is_recurring)) {
            return 'Please specify whether this cash request is recurring.';
          }
        } else if (this.isTradeRequestCase) {
          if (this.isBlank(this.form.fields.trade_request_type)) {
            return 'Please select a trade request type.';
          }
          if (this.isBlank(this.form.fields.trade_request_account_numbers)) {
            return 'Please enter account number(s) for the trade request.';
          }
          if (this.isBlank(this.form.fields.trade_request_instructions)) {
            return 'Please provide trade instructions.';
          }
          if (this.isBlank(this.form.fields.trade_request_custodian)) {
            return 'Please select at least one custodian for the trade request.';
          }
        } else if (this.isIpsRequestCase) {
          if (this.isBlank(this.form.fields.ips_request_type)) {
            return 'Please select an IPS request type.';
          }
          if (this.isBlank(this.form.fields.ips_account_number)) {
            return 'Please enter an account number for the IPS request.';
          }
          if (this.isBlank(this.form.fields.ips_custodian)) {
            return 'Please select at least one custodian for the IPS request.';
          }
          if (this.isBlank(this.form.fields.ips_request_details)) {
            return 'Please provide IPS request details.';
          }
        } else if (this.isTradeReviewCase) {
          if (this.isBlank(this.form.fields.trade_review_type)) {
            return 'Please select a trade review type.';
          }
          if (this.isBlank(this.form.fields.trade_review_account_number)) {
            return 'Please enter an account number for the trade review.';
          }
          if (this.isBlank(this.form.fields.trade_review_custodian)) {
            return 'Please select at least one custodian for the trade review.';
          }
        } else if (this.isAdvocacyCase) {
          if (this.isBlank(this.form.fields.advocacy_account_number)) {
            return 'Please enter an account number for the advocacy request.';
          }
          if (this.isBlank(this.form.fields.advocacy_custodian)) {
            return 'Please select at least one custodian for the advocacy request.';
          }
        } else if (this.isVadisQuestionCase && this.isBlank(this.form.fields.vadis_question)) {
          return 'Please enter the Vadis question details.';
        } else if (this.isReoptimizationCase) {
          if (this.isBlank(this.form.fields.reoptimization_account_identifier)) {
            return 'Please enter an account number or account name for reoptimization.';
          }
          if (this.isBlank(this.form.fields.reoptimization_custodian)) {
            return 'Please select at least one custodian for reoptimization.';
          }
        }
      }
      return '';
    },
    validateForm() {
      if (this.isBlank(this.form.fields.subject)) {
        return 'Please enter a case subject before submitting.';
      }
      if (this.isBlank(this.form.priority)) {
        return 'Please select a priority before submitting.';
      }
      if (this.requiresEntitySelection) {
        if (this.isBlank(this.effectiveEntityExternalId)) {
          return 'Please select a client before submitting this request type.';
        }
        if (this.isBlank(this.effectiveEntityName)) {
          return 'Selected client is missing a name. Please select a valid client.';
        }
      }
      for (let stepNumber = 1; stepNumber <= 3; stepNumber += 1) {
        const stepError = this.validateStep(stepNumber);
        if (stepError) {
          return stepError;
        }
      }
      return '';
    },
    buildSubmissionFields() {
      const fields = {
        subject: this.form.fields.subject,
        request_category: this.submissionRequestCategory,
      };
      if (this.isNewAccountCase || this.isCloseAccountCase || this.isServiceOther) {
        fields.service_case_type = this.form.fields.service_case_type;
        if (this.isNewAccountCase) {
          fields.new_account_custodian = this.form.fields.new_account_custodian;
          fields.new_account_account_type = this.form.fields.new_account_account_type;
          fields.new_account_description = this.form.fields.new_account_description;
        } else if (this.isCloseAccountCase) {
          fields.close_account_number = this.form.fields.close_account_number;
          fields.close_account_custodian = this.form.fields.close_account_custodian;
          fields.close_account_sec_reason = this.form.fields.close_account_sec_reason;
          fields.close_account_description = this.form.fields.close_account_description;
        } else if (this.isOrionRequestCase) {
          fields.orion_request_custodian = this.form.fields.orion_request_custodian;
          fields.orion_request_account_identifier = this.form.fields.orion_request_account_identifier;
          fields.orion_request_details = this.form.fields.orion_request_details;
        } else if (this.isClientRecordChangeCase) {
          fields.client_record_change_type = this.form.fields.client_record_change_type;
          fields.client_record_change_account_identifier = this.form.fields.client_record_change_account_identifier;
          fields.client_record_change_custodian = this.form.fields.client_record_change_custodian;
          fields.client_record_change_details = this.form.fields.client_record_change_details;
        } else if (this.isBusinessDevelopmentCase) {
          fields.business_development_type = this.form.fields.business_development_type;
          fields.business_development_details = this.form.fields.business_development_details;
        } else if (this.isAdvisorOrgMaintenanceCase) {
          fields.advisor_org_maintenance_type = this.form.fields.advisor_org_maintenance_type;
          fields.advisor_org_maintenance_details = this.form.fields.advisor_org_maintenance_details;
        } else if (this.isQuestionsCase) {
          fields.questions_case_type = this.form.fields.questions_case_type;
          fields.fafn_question = this.form.fields.fafn_question;
        } else if (this.isComplianceCase) {
          fields.compliance_case_type = this.form.fields.compliance_case_type;
          fields.compliance_details = this.form.fields.compliance_details;
          if (this.isComplianceFraudAlertCase) {
            fields.compliance_account_number = this.form.fields.compliance_account_number;
            fields.compliance_custodian = this.form.fields.compliance_custodian;
          }
        }
      } else if (this.isTradingRelated) {
        fields.trading_case_type = this.form.fields.trading_case_type;
        if (this.isRaiseCashDistributeCase) {
          fields.distribution_amount = this.form.fields.distribution_amount;
          fields.distribution_account_number = this.form.fields.distribution_account_number;
          fields.distribution_delivery_instructions = this.form.fields.distribution_delivery_instructions;
          fields.distribution_modify_recurring = this.form.fields.distribution_modify_recurring;
          fields.distribution_custodian = this.form.fields.distribution_custodian;
        } else if (this.isCashAmountCase) {
          fields.cash_transfer_into_existing_account = this.form.fields.cash_transfer_into_existing_account;
          fields.cash_amount = this.form.fields.cash_amount;
          fields.cash_is_recurring = this.form.fields.cash_is_recurring;
          fields.cash_account_number = this.form.fields.cash_account_number;
          fields.cash_custodian = this.form.fields.cash_custodian;
        } else if (this.isTradeRequestCase) {
          fields.trade_request_type = this.form.fields.trade_request_type;
          fields.trade_request_account_numbers = this.form.fields.trade_request_account_numbers;
          fields.trade_request_instructions = this.form.fields.trade_request_instructions;
          fields.trade_request_custodian = this.form.fields.trade_request_custodian;
        } else if (this.isIpsRequestCase) {
          fields.ips_request_type = this.form.fields.ips_request_type;
          fields.ips_account_number = this.form.fields.ips_account_number;
          fields.ips_custodian = this.form.fields.ips_custodian;
          fields.ips_request_details = this.form.fields.ips_request_details;
        } else if (this.isTradeReviewCase) {
          fields.trade_review_type = this.form.fields.trade_review_type;
          fields.trade_review_account_number = this.form.fields.trade_review_account_number;
          fields.trade_review_custodian = this.form.fields.trade_review_custodian;
          fields.trade_review_instructions = this.form.fields.trade_review_instructions;
        } else if (this.isAdvocacyCase) {
          fields.advocacy_account_number = this.form.fields.advocacy_account_number;
          fields.advocacy_custodian = this.form.fields.advocacy_custodian;
          fields.advocacy_instruction = this.form.fields.advocacy_instruction;
        } else if (this.isVadisQuestionCase) {
          fields.vadis_question = this.form.fields.vadis_question;
        } else if (this.isReoptimizationCase) {
          fields.reoptimization_account_identifier = this.form.fields.reoptimization_account_identifier;
          fields.reoptimization_custodian = this.form.fields.reoptimization_custodian;
          fields.reoptimization_instruction = this.form.fields.reoptimization_instruction;
        }
      }
      return fields;
    },
    resolveCaseDescription() {
      if (this.isNewAccountCase) {
        return String(this.form.fields.new_account_description || '').trim();
      }
      if (this.isCloseAccountCase) {
        return String(this.form.fields.close_account_description || '').trim();
      }
      if (this.isAdvocacyCase) {
        return String(this.form.fields.advocacy_instruction || '').trim();
      }
      if (this.isReoptimizationCase) {
        return String(this.form.fields.reoptimization_instruction || '').trim();
      }
      return '';
    },
    async submitCase() {
      this.formError = '';
      const validationError = this.validateForm();
      if (validationError) {
        this.formError = validationError;
        return;
      }
      this.isSubmittingCase = true;
      try {
        let attachmentUploadFailed = false;
        const payload = {
          type: this.form.type,
          subject: this.form.fields.subject,
          priority: this.form.priority,
          description: this.resolveCaseDescription(),
          initial_comment: this.initialComment.trim(),
          fields: this.buildSubmissionFields(),
        };
        if (this.effectiveEntityExternalId) {
          payload.entity_external_id = this.effectiveEntityExternalId;
        }
        const response = await fetch(this.submitUrl, {
          method: 'POST',
          headers: getJsonHeaders(this.$root.$data.csrfToken),
          body: JSON.stringify(payload),
        });
        if (!response.ok) {
          const errorData = await response.json();
          this.formError = errorData?.error || 'Failed to submit case';
          Sentry.captureMessage('Case submission request failed', {
            level: 'warning',
            extra: {
              submitUrl: this.submitUrl,
              responseStatus: response.status,
              error: this.formError,
            },
          });
          return;
        }
        const data = await response.json();
        const submittedCase = data?.case || data || null;
        const caseId = submittedCase?.id;
        if (caseId && this.pendingAttachmentFiles.length > 0) {
          try {
            await this.uploadAttachments(caseId);
          } catch (error) {
            attachmentUploadFailed = true;
            console.error(error);
            Sentry.captureException(error, {
              tags: {
                feature: 'case_submission',
                operation: 'upload_case_attachment',
              },
              extra: {
                caseId,
                submitUrl: this.submitUrl,
                pendingAttachmentCount: this.pendingAttachmentFiles.length,
                uploadContext: error?.uploadContext || null,
                rootCause: error?.cause?.message || '',
              },
            });
          }
        }
        this.$emit('submitted', {
          data,
          attachmentUploadFailed,
        });
        this.form = {
          priority: '',
          type: 'General Support',
          fields: emptyCaseFields(),
        };
        this.initialComment = '';
        this.pendingAttachmentFiles = [];
        this.formError = '';
        this.clearSelectedEntity();
      } catch (error) {
        console.error(error);
        Sentry.captureException(error);
        this.formError = 'Failed to submit case';
      } finally {
        this.isSubmittingCase = false;
      }
    },
  },
};
</script>

<style scoped>
.form-section {
  display: flex;
  flex-direction: column;
  gap: var(--stk-size-4);
  margin-bottom: var(--stk-size-6);
  max-width: 52rem;
  border: 1px solid var(--stk-color-border);
  border-radius: var(--stk-radius-2);
  background: var(--stk-color-bg-primary);
}

.form-actions {
  display: flex;
  gap: var(--stk-size-3);
}

.button-loading-content {
  display: inline-flex;
  align-items: center;
  gap: var(--stk-size-2);
}

.client-row {
  display: flex;
  gap: var(--stk-size-3);
  align-items: center;
}

.client-row > :first-child {
  flex: 1;
}

.form-radios {
  display: grid;
  gap: var(--stk-size-2);
}

.radios-wide {
  grid-template-columns: repeat(auto-fit, minmax(15rem, 1fr));
  gap: var(--stk-size-3);
}

.form-intro {
  color: var(--stk-color-text-muted);
  margin-top: calc(var(--stk-size-2) * -1);
}

.step-heading {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--stk-color-text-muted);
  margin: var(--stk-size-2) 0 calc(var(--stk-size-2) * -1);
}

.case-textarea {
  min-height: 6rem;
  resize: vertical;
}

.hint {
  color: var(--stk-color-text-muted);
  font-size: 0.85rem;
  margin-top: var(--stk-size-2);
}

.form-error {
  color: var(--stk-color-danger);
}
</style>
